import React, { createContext, useCallback, useEffect, useState } from "react";
import { prokeyApi } from 'api/prokey';
import {getFocusedField, getFocusedImage, onBlockFocus, onFieldFocusImage, onImageMain, removeListener} from "api/prokey/prokey.websocket";

//-----------------------------------------------------------------
export function Canvas(props) {
  //-----------------------------------------------------------------

  //#region [VALUES] 

  const {
    className,
  } = props;

  // the canvas' context is stored once it's created
  const [ image_data , setImageData ] = useState( getFocusedImage() );
  const [ image      , setImage     ] = useState( null );
  const [ field      , setField     ] = useState( getFocusedField() );
  const [ block      , setBlock     ] = useState(  {}  );

  const [ ref_base        , setRefBase        ] = useState( null );
  const [ ref_magnify     , setRefMagnify     ] = useState( null );
  const [ context_base    , setContextBase    ] = useState( null );
  const [ context_magnify , setContextMagnify ] = useState( null );

  const [ image_width   , setImageWidth   ] = useState( 1 );
  const [ image_height  , setImageHeight  ] = useState( 1 );
  const [ canvas_width  , setCanvasWidth  ] = useState( 1 );
  const [ canvas_height , setCanvasHeight ] = useState( 1 );  
  const [ width_conversion  , setConversionWidth  ] = useState( 1 );
  const [ height_conversion , setConversionHeight ] = useState( 1 );

  const [ canvas_highlights , setCanvasHighlights ] = useState( {} );

  const setBaseRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d");
      setCanvasWidth ( context.canvas.clientWidth  );
      setCanvasHeight( context.canvas.clientHeight );
      setContextBase( context );
      setRefBase( node );
    }
  }, []);

  const setMagnifyRefNode = useCallback(node => {
    if (node !== null) {
      const context = node.getContext("2d")
      setContextMagnify( context );
      setRefMagnify( node );
    }
  }, []);

  let selected_entry = null;

  let showMagnifier = false;
  let drawHighlight = false;

  let startCoord = null;
  let endCoord = null;

  const buffer_x = 10;
  const buffer_y = 10;

  let mouse_x = 0;
  let mouse_y = 0;

  //#endregion

  //#region [STYLES]

  const _styles = {
    BASE: {
      position  : 'absolute' ,
      top       : 0          ,
      left      : 0          ,
      maxWidth  : '100%'     ,
      maxHeight : '100%'     ,
      zIndex    : 20         ,
    }
  }

  //#endregion

  //#region [EFFECT]

  // ===

  useEffect(() => {
    if( !image ) return;

    setImageWidth ( image.naturalWidth  );
    setImageHeight( image.naturalHeight );
  
    let rotated = rotatePoint( image.naturalWidth, image.naturalHeight, image_data.info.rotation );
    setConversionWidth(rotated.x / image.naturalWidth);
    setConversionHeight(rotated.y / image.naturalHeight);
  }, [image, canvas_width, canvas_height]);

  // ===

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      setImageData(data)
    }
    //-----------------------------------------------------------------
    function handleField(data) {
      //-----------------------------------------------------------------
      setField(data)
    }
    //-----------------------------------------------------------------
    function handleBlockFocus(data) {
      //-----------------------------------------------------------------
      if( block.id === data.id) return;
      setBlock(data);
    }

    onImageMain      ( handleImage      );
    onFieldFocusImage( handleField      );
    onBlockFocus     ( handleBlockFocus );
    
    return () => {
      removeListener( 'IMAGE'       , handleImage      );
      removeListener( 'FIELD-FOCUS' , handleField      );
      removeListener( 'BLOCK-FOCUS' , handleBlockFocus );
    }
  }, [] );

  useEffect( () => {
    if( !image_data ) return;
    const temp = document.createElement('img');

    temp.onload = function () {
      setImage( temp );  
    };

    temp.src = image_data.url;
  }, [image_data] )

  useEffect( () => {
  
    //-----------------------------------------------------------------
    function createFieldHighlights( ) {
      //-----------------------------------------------------------------
      if( !field || !image) return;
  
      const canvas_buffers = {};

      const field_buffer = { id: 1, entries: new Map(), is_current: true }
      canvas_buffers[ 1 ] = field_buffer;

      const entry = field.Entries[0];
      if( entry.PageNr !== image_data.info.PageNr ) {
        setCanvasHighlights( canvas_buffers );
        return;
      }

      const is_selected = false;//index === selected_entry;

      let coords = getHighlightRect(entry);

      const canvas = document.createElement('canvas')
      canvas.width  = coords.width  + 4 ;
      canvas.height = coords.height + 4 ;
      const buffer = { canvas, x: coords.x, y: coords.y, is_selected };
      
      createFieldHighlight( canvas, coords, true, is_selected );
      field_buffer.entries.set( coords, buffer );

      setCanvasHighlights( canvas_buffers );
    }
  
    //-----------------------------------------------------------------
    function createFieldHighlight( canvas, { x, y, width, height }, is_curr_field, is_selected) {
      //-----------------------------------------------------------------
      const buffer_context = canvas.getContext('2d');
      buffer_context.drawImage( image, x-2, y-2, width+4, height+4, 0, 0, width, height );
  
      const color = is_curr_field ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'
  
      buffer_context.globalCompositeOperation = "darken";
  
      buffer_context.beginPath();
  
      if( is_curr_field && is_selected ) {
        buffer_context.strokeStyle = "black";
        buffer_context.strokeRect(x-2, y-2, width, height);
      }
  
      buffer_context.fillStyle = color;
      buffer_context.fillRect(0, 0, width, height);
  
      buffer_context.closePath();
    }

    createFieldHighlights(  );
  }, [field, image] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function draw() {
      //-----------------------------------------------------------------
      if( !context_base || !image ) return;
      if( image_width <= 1) return;
  
      context_base.clearRect(0, 0, image_width, image_height);
      context_base.beginPath();
      context_base.drawImage(image, 0, 0, image_width, image_height);
      drawFieldHighlights();
      context_base.closePath();
    }

    //-----------------------------------------------------------------
    function drawFieldHighlights() {
      //-----------------------------------------------------------------
      Object.values( canvas_highlights ).forEach( _field => {
        _field.entries.forEach( (highlight) => {
          context_base.drawImage( highlight.canvas, highlight.x, highlight.y );
        })
      })
    }
    
    draw();
  }, [image, context_base, canvas_highlights, image_width, image_height] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function rotatePoint(x, y, degree) {
    //-----------------------------------------------------------------
    const radian = degree * (Math.PI/180);
    const newX = Math.cos(radian) * x - Math.sin(radian) * y;
    const newY = Math.sin(radian) * x + Math.cos(radian) * y;
    return {x: Math.abs(newX), y: Math.abs(newY)};
  }


  //-----------------------------------------------------------------
  function getHighlightRect(entry) {
    //-----------------------------------------------------------------
    let skewedTopLeft = skew(entry.Left, entry.Top);
    let skewedBottomRight = skew(entry.Right, entry.Bottom);

    let skewedX = parseInt(skewedTopLeft.x * width_conversion);
    let skewedY = parseInt(skewedTopLeft.y * height_conversion);
    let skewedWidth = parseInt(skewedBottomRight.x * width_conversion) - skewedX;
    let skewedHeight = parseInt(skewedBottomRight.y * height_conversion) - skewedY;
    
    return { x: skewedX, y: skewedY, width: skewedWidth, height: skewedHeight};
  }

  //-----------------------------------------------------------------
  function skew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (image_data.info.deskew_alpha*3.141592654)/180.0 );

    if( image_data.info.deskew_mode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : image_data.info.deskew_height;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x -= x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y += y_change;

    return result;
  }

  //-----------------------------------------------------------------
  function deskew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (image_data.info.deskew_alpha*3.141592654)/180.0 );

    if( image_data.info.deskew_mode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : image_data.info.deskew_height;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x += x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y -= y_change;

    return result;
  }

  //-----------------------------------------------------------------
  function highlightCoordsStart() {
    //-----------------------------------------------------------------
    if(drawHighlight) return;

    drawHighlight = true;
    const rect = ref_base.getBoundingClientRect();
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    let xx = parseInt(((mouse_x - rect.left) * x_conv));
    let yy = parseInt(((mouse_y - rect.top) * y_conv));

    endCoord = null;
    startCoord = {x:xx,y:yy};
  }

  //-----------------------------------------------------------------
  function highlightCoordsEnd() {
    //-----------------------------------------------------------------
    if(!drawHighlight) return;

    const rect = ref_base.getBoundingClientRect();
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    const x = parseInt(((mouse_x - rect.left) * x_conv));
    const y = parseInt(((mouse_y - rect.top) * y_conv));
    endCoord = {x,y};
  }

  //-----------------------------------------------------------------
  function saveHighlightCoords() {
    //-----------------------------------------------------------------
    if(!drawHighlight) return;
    const isEndBeforeStart_X = startCoord.x > endCoord.x
    const isEndBeforeStart_Y = startCoord.y > endCoord.y
    const start_x = isEndBeforeStart_X ? endCoord.x : startCoord.x
    const end_x = isEndBeforeStart_X ? startCoord.x : endCoord.x
    const start_y = isEndBeforeStart_Y ? endCoord.y : startCoord.y
    const end_y = isEndBeforeStart_Y ? startCoord.y : endCoord.y
    const topLeft = deskew(start_x / width_conversion, start_y / height_conversion);
    const bottomRight = deskew(end_x / width_conversion, end_y / height_conversion);
    prokeyApi.updateFieldCoords({
      id: field?.Id,
      imageId: image_data.id,
      content: field?.Entries[0].Content,
      left: topLeft.x,
      top: topLeft.y,
      right: bottomRight.x,
      bottom: bottomRight.y,
    });
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onMouseDown(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if( event.button === 0 ) {
      showMagnifier = true;
      drawMagnifier();
    }
  }

  //-----------------------------------------------------------------
  function onMouseUp(event) {
    //-----------------------------------------------------------------
    // Left mouse button
    if( event.button === 0 ) {
      showMagnifier = false;
      saveHighlightCoords();
      drawMagnifier();
      drawHighlight = false;
    }
  }

  //-----------------------------------------------------------------
  function onMouseLeave(event) {
    //-----------------------------------------------------------------
    saveHighlightCoords();
    drawMagnifier();
    showMagnifier = false;
  }

  //-----------------------------------------------------------------
  function onMouseMove(event) {
    //-----------------------------------------------------------------
    mouse_x = event.clientX;
    mouse_y = event.clientY;
    const rect = ref_base.getBoundingClientRect();
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    const x = parseInt(((mouse_x - rect.left) * x_conv));
    const y = parseInt(((mouse_y - rect.top) * y_conv));
    // const skewed = skew(x,y);

    selected_entry = null;
    // check if mouse if over field highlight
    if( !field ) return;
    for(let index = field.Entries.length-1; index >= 0; index--) {
      const entry = field.Entries[index];
      const entry_rect = getHighlightRect(entry);
      const is_inside_x = x > entry_rect.x-buffer_x && x < entry_rect.x + entry_rect.width+buffer_x;
      const is_inside_y = y > entry_rect.y-buffer_y && y < entry_rect.y + entry_rect.height+buffer_y;
      if(is_inside_x && is_inside_y){
        selected_entry = index;
        break;
      }
    }

    highlightCoordsEnd();
    if(showMagnifier) drawMagnifier();
  }

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if(showMagnifier) {
      const coord_count = field.Entries[0].Bottom < 0 ? 0 : field.Entries.length;
      const is_coord_allowed = coord_count === 0 || field.validationInfo.AllowMultipleCoords;
      if(event.ctrlKey && is_coord_allowed) highlightCoordsStart();
    }

    if(event.key === 'Delete' && selected_entry!==null) {
      prokeyApi.removeEntry(field.Id,selected_entry);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function drawMagnifier() {
    //-----------------------------------------------------------------
    if(!showMagnifier) {
      context_magnify.clearRect(0, 0, image_width, image_height);
      return;
    }
    const rect = ref_base.getBoundingClientRect();
    const width = 400;
    const height = 200;
    const x_conv = image_width  / context_base.canvas.clientWidth  ;
    const y_conv = image_height / context_base.canvas.clientHeight ;
    let xx = parseInt(((mouse_x - rect.left) * x_conv));
    let yy = parseInt(((mouse_y - rect.top) * y_conv));
    var buffer = document.createElement('canvas');
    buffer.width  = width ;
    buffer.height = height;

    buffer.getContext('2d').drawImage(context_base.canvas,xx-width*0.5, yy-height*0.5, width, height, 0,0, width, height );

    context_magnify.clearRect(0, 0, image_width, image_height);
    context_magnify.beginPath();
    context_magnify.drawImage(buffer,xx-width,yy-height,width*2, height*2);
    context_magnify.rect(xx-width,yy-height,width*2, height*2);
    context_magnify.stroke();
    context_magnify.closePath();
  }

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <>
        <canvas
          style={ _styles.BASE }
          ref={setBaseRefNode}
          height={image_height}
          width={image_width}        
          tabIndex={999}
          onMouseUp={onMouseUp}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          onKeyDown={onKeyDown}
        />
        <canvas
          style={ _styles.BASE }
          ref={setMagnifyRefNode}
          height={image_height}
          width={image_width}        
          tabIndex={999}
          onMouseUp={onMouseUp}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          onKeyDown={onKeyDown}
        />
      </>
    );
  }

  //#endregion

  return display();
};
